import { useId, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import {
  useCompliance,
  useMarkDone,
  useReopen,
  useSaveCompliance,
  useSetArchived,
  type ComplianceItem,
  type ComplianceStatus,
} from "@/api/compliance";
import { ComplianceForm } from "@/components/forms/ComplianceForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { PageHeader } from "@/components/ui/page-header";
import { accrualText } from "@/lib/compliance";
import { todayIso } from "@/lib/dates";
import { formatDate, formatInr } from "@/lib/format";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type Panel =
  | { kind: "new" }
  | { kind: "edit"; item: ComplianceItem }
  | { kind: "done"; item: ComplianceItem }
  | null;

const GROUPS: { status: ComplianceStatus; title: string }[] = [
  { status: "overdue", title: "Overdue" },
  { status: "due_soon", title: "Due within 30 days" },
  { status: "upcoming", title: "Later" },
  { status: "done", title: "Done" },
];

const TONE: Record<ComplianceStatus, "destructive" | "warning" | "muted" | "success"> = {
  overdue: "destructive",
  due_soon: "warning",
  upcoming: "muted",
  done: "success",
};

function statusText(item: ComplianceItem): string {
  if (item.status === "done") return `Done ${formatDate(item.done_on)}`;
  if (item.status === "overdue") return `${item.days_late} days late`;
  return `Due ${formatDate(item.due_on)}`;
}

function feeText(item: ComplianceItem): string {
  if (item.accrued_fee_inr === null) return "No daily fee";
  const perDay = `${formatInr(item.daily_fee_inr ?? "0")} a day`;
  if (item.status === "done") return `${formatInr(item.accrued_fee_inr)} paid late`;
  if (item.status !== "overdue") return `${perDay} once late`;
  return `${formatInr(item.accrued_fee_inr)} accrued, +${perDay}`;
}

function panelTitle(panel: Exclude<Panel, null>): string {
  if (panel.kind === "new") return "New obligation";
  const label = `${panel.item.form} ${panel.item.period_label}`;
  return panel.kind === "edit" ? `Edit ${label}` : `Mark ${label} done`;
}

function DoneForm({
  item,
  onDone,
  onCancel,
}: {
  item: ComplianceItem;
  onDone: () => void;
  onCancel: () => void;
}) {
  const id = useId();
  const markDone = useMarkDone();
  const { values, set } = useFormValues({
    done_on: todayIso(),
    filing_ref: "",
    create_next: item.recurrence === "annual",
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(async () => {
      await markDone.mutateAsync({
        id: item.id,
        input: {
          done_on: values.done_on,
          filing_ref: values.filing_ref.trim(),
          create_next: values.create_next,
        },
        version: item.version,
      });
      onDone();
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onCancel} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-on`}
          label="Done on"
          type="date"
          value={values.done_on}
          onChange={(value) => set("done_on", value)}
          error={errors.done_on}
        />
        <TextField
          id={`${id}-ref`}
          label="Filing reference"
          value={values.filing_ref}
          onChange={(value) => set("filing_ref", value)}
          error={errors.filing_ref}
          hint="SRN or acknowledgement number - optional"
        />
      </div>
      {item.recurrence === "annual" && (
        <Checkbox
          id={`${id}-next`}
          label="Add next year's row"
          checked={values.create_next}
          onChange={(value) => set("create_next", value)}
        />
      )}
      <FormActions saving={saving} submitLabel="Mark done" onCancel={onCancel} />
    </form>
  );
}

function ItemRow({
  item,
  panelOpen,
  onEdit,
  onMarkDone,
}: {
  item: ComplianceItem;
  panelOpen: boolean;
  onEdit: () => void;
  onMarkDone: () => void;
}) {
  const reopen = useReopen();
  const setArchived = useSetArchived();
  const busy = reopen.isPending || setArchived.isPending;
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-5">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          {item.form} · {item.period_label}
          <Badge variant={TONE[item.status]}>{statusText(item)}</Badge>
          {item.archived && <Badge variant="muted">Archived</Badge>}
        </p>
        <p className="text-sm">
          {item.name}
          {item.provision ? ` (${item.provision})` : ""}
        </p>
        <p className="text-xs text-muted-foreground">
          {[
            `Due ${formatDate(item.due_on)}`,
            feeText(item),
            item.responsible,
            item.filing_ref && `Ref ${item.filing_ref}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <RequestError error={reopen.error ?? setArchived.error} />
      </div>
      {!panelOpen && !item.archived && (
        <div className="flex flex-wrap gap-2">
          {item.status === "done" ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => reopen.mutate({ id: item.id, version: item.version })}
            >
              Reopen
            </Button>
          ) : (
            <Button size="sm" onClick={onMarkDone}>
              Mark done
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onEdit}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => setArchived.mutate({ id: item.id, archived: true, version: item.version })}
          >
            Archive
          </Button>
        </div>
      )}
      {item.archived && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => setArchived.mutate({ id: item.id, archived: false, version: item.version })}
        >
          Restore
        </Button>
      )}
    </li>
  );
}

export function CompliancePage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [panel, setPanel] = useState<Panel>(null);
  const compliance = useCompliance(includeArchived);
  const save = useSaveCompliance();
  const rows = compliance.data ?? [];
  const overdue = rows.filter((row) => row.status === "overdue" && !row.archived);
  const perDayCents = overdue.reduce(
    (sum, row) => sum + Math.round(Number(row.daily_fee_inr ?? 0) * 100),
    0,
  );
  const accrual = accrualText({
    accrued_inr: (
      overdue.reduce((sum, row) => sum + Math.round(Number(row.accrued_fee_inr ?? 0) * 100), 0) /
      100
    ).toFixed(2),
    accruing_per_day_inr: (perDayCents / 100).toFixed(2),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Compliance"
        description="ROC forms, tax returns and meetings: what is due, what is done, and what each day of delay costs."
        actions={
          panel === null && (
            <Button onClick={() => setPanel({ kind: "new" })}>
              <Plus className="h-4 w-4" aria-hidden />
              Add obligation
            </Button>
          )
        }
      />
      {overdue.length > 0 && (
        <Alert
          variant="error"
          title={`${overdue.length} overdue: ${accrual}`}
        >
          These figures come from the company's own dates and the Companies Act fee table. Your
          CA or CS confirms what is actually payable.
        </Alert>
      )}

      {panel && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>{panelTitle(panel)}</CardTitle>
          </CardHeader>
          <CardContent>
            {panel.kind === "done" ? (
              <DoneForm
                item={panel.item}
                onDone={() => setPanel(null)}
                onCancel={() => setPanel(null)}
              />
            ) : (
              <ComplianceForm
                key={panel.kind === "edit" ? `${panel.item.id}-${panel.item.version}` : "new"}
                initial={panel.kind === "edit" ? panel.item : null}
                submitLabel={panel.kind === "new" ? "Add obligation" : "Save"}
                onSubmit={async (input) => {
                  await save.mutateAsync({
                    input,
                    existing:
                      panel.kind === "edit"
                        ? { id: panel.item.id, version: panel.item.version }
                        : undefined,
                  });
                  setPanel(null);
                }}
                onCancel={() => setPanel(null)}
                onReload={() => {
                  void compliance.refetch();
                  setPanel(null);
                }}
              />
            )}
          </CardContent>
        </Card>
      )}

      <Checkbox
        id="compliance-archived"
        label="Show archived"
        checked={includeArchived}
        onChange={setIncludeArchived}
      />

      {compliance.isPending ? (
        <p className="text-muted-foreground">Loading the calendar…</p>
      ) : compliance.isError ? (
        <RequestError error={compliance.error} />
      ) : (
        GROUPS.map((group) => {
          const inGroup = rows.filter((row) => row.status === group.status);
          if (inGroup.length === 0) return null;
          return (
            <section key={group.status} className="flex flex-col gap-2">
              <h2 className="text-base font-semibold">
                {group.title}{" "}
                <span className="font-sans text-sm font-normal text-muted-foreground">
                  ({inGroup.length})
                </span>
              </h2>
              <ul className="divide-y overflow-hidden rounded-lg border bg-card shadow-sm">
                {inGroup.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    panelOpen={panel !== null}
                    onEdit={() => setPanel({ kind: "edit", item })}
                    onMarkDone={() => setPanel({ kind: "done", item })}
                  />
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
